"use client";
import { useUserLocation } from "@/context/UserLocationContext";
import MapPinIcon from "@heroicons/react/24/solid/MapPinIcon";
import { useTranslation } from "@/hooks/translationContext";

export default function LocationToggleBar() {
  const { t } = useTranslation();
  const {
    locationPermission,
    requestLocationPermission,
    disableLocation,
  } = useUserLocation();

  const isActive = locationPermission === "granted";
  const isLoading = locationPermission === "loading";

  const handleToggle = () => {
    if (isLoading) return;
    if (isActive) {
      disableLocation();
      return;
    }
    requestLocationPermission();
  };

  const label = (() => {
    if (isLoading) return t("location_toggle_loading", { defaultValue: "Konum alınıyor..." });
    if (isActive) return t("location_toggle_active", { defaultValue: "Konum açık" });
    if (locationPermission === "denied") return t("location_toggle_denied", { defaultValue: "Konum izni reddedildi" });
    if (locationPermission === "unavailable") return t("location_toggle_unavailable", { defaultValue: "Konum alınamadı" });
    return t("location_toggle_prompt", { defaultValue: "Yakınımdaki işletmeleri gör" });
  })();

  const sublabel = (() => {
    if (locationPermission === "denied") return t("location_toggle_denied_hint", { defaultValue: "Tarayıcı ayarlarından konum iznini açın" });
    if (locationPermission === "unavailable") return t("location_toggle_unavailable_hint", { defaultValue: "Cihaz konum servisleri kapalı olabilir" });
    return null;
  })();

  return (
    <div className="w-full flex justify-center mt-8 mb-2">
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`
          flex items-center gap-3 px-4 py-2.5 rounded-full border transition-all text-sm
          ${isActive
            ? "bg-green-50 border-green-200 text-green-700 cursor-pointer"
            : locationPermission === "denied" || locationPermission === "unavailable"
              ? "bg-gray-50 border-gray-200 text-gray-500"
              : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 cursor-pointer"
          }
          ${isLoading ? "opacity-70" : ""}
        `}
      >
        <MapPinIcon className={`size-4 shrink-0 ${isActive ? "text-green-600" : "text-gray-400"}`} />

        <div className="flex flex-col items-start">
          <span className="leading-tight">{label}</span>
          {sublabel && (
            <span className="text-[11px] text-gray-400 leading-tight">{sublabel}</span>
          )}
        </div>

        {/* Toggle switch visual */}
        <div className={`
          relative w-9 h-5 rounded-full transition-colors shrink-0 ml-1
          ${isActive ? "bg-green-500" : "bg-gray-300"}
        `}>
          <div className={`
            absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
            ${isActive ? "translate-x-4" : "translate-x-0.5"}
          `} />
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </button>
    </div>
  );
}
